import { UseNotifications, useNotifications } from '@elearning-platform/ui';
import { TransProps, useTranslation } from 'react-i18next';

import { generateToken, verifyToken } from '../api/requests';
import { FrontendPhpApiError } from '../interfaces/api';
import { UseFormsVerification } from '../interfaces/hooks';

export const useFormsVerification: () => UseFormsVerification = (): UseFormsVerification => {
  const { t }: TransProps<never> = useTranslation();
  const {
    showCodePopup,
    setCodePopupHasError,
    setCodePopupButtonIsLoading,
    hideCodePopup,
    addToast,
  }: UseNotifications = useNotifications();

  const verifyFormWithEmailToken: (
    formId: number, facsimileFieldValue: string, fieldPath?: string
  ) => Promise<boolean> = (
    formId: number, facsimileFieldValue: string, fieldPath?: string
  ): Promise<boolean> => new Promise<boolean>((resolve: (isTokenVerified: boolean) => void): void => {
    generateToken(formId, facsimileFieldValue, fieldPath)
      .catch((error: FrontendPhpApiError): void => {
        if (error?.response?.data?.message) {
          addToast({ text: error?.response?.data?.message });
        }
      });
    showCodePopup({
      title: t('toyota.app.useFormsVerification.codePopup.title'),
      resendAction: (): void => {
        generateToken(formId, facsimileFieldValue.toString())
          .catch((error: FrontendPhpApiError): void => {
            if (error?.response?.data?.message) {
              addToast({ text: error?.response?.data?.message });
            }
          });
      },
      acceptButtonData: {
        label: t('toyota.app.useFormsVerification.codePopup.acceptButtonLabel'),
        onClick: (code: string): void => {
          setCodePopupHasError(false);
          setCodePopupButtonIsLoading('primary', true);
          verifyToken(formId, code)
            .then((): void => resolve(true))
            .catch((): void => {
              setCodePopupButtonIsLoading('primary', false);
              setCodePopupHasError(true);
            });
        },
      },
      cancelButtonData: {
        label: t('toyota.app.useFormsVerification.codePopup.cancelButtonLabel'),
        onClick: (): void => {
          resolve(false);
          hideCodePopup();
        },
      },
      onClose: (): void => {
        resolve(false);
        hideCodePopup();
      },
      description: t('toyota.app.useFormsVerification.codePopup.description'),
      rightBottomImg: 'default',
    });
  });

  const defaultActionsAfterVerifyFormWithEmailToken: () => void = (): void => {
    hideCodePopup();
  };

  return { verifyFormWithEmailToken, defaultActionsAfterVerifyFormWithEmailToken };
};
